import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Search, Trash2, Mail, Building2, Calendar, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourceLeadsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/resource-leads`, { headers: getAuthHeaders() });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load resource leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [getAuthHeaders]);

  const handleDelete = async (leadId) => {
    try {
      await axios.delete(`${API}/resource-leads/${leadId}`, { headers: getAuthHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      toast.success('Lead removed');
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const filtered = leads.filter((l) => {
    const q = searchTerm.toLowerCase();
    return (
      l.full_name?.toLowerCase().includes(q) ||
      l.organization?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q)
    );
  });

  const copyShareLink = () => {
    const url = `${window.location.origin}/resources`;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Public resource link copied to clipboard');
    } catch {
      toast.info(`Share this link: ${url}`);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Resource Leads</h1>
          <p className="text-sm text-[#64748B]">Prospects who accessed the NIL Resource Library</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-[#E2E8F0] text-[#475569]"
            onClick={copyShareLink}
            data-testid="copy-share-link-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Copy Public Link
          </Button>
          <Badge className="bg-[#6B5CE7]/10 text-[#6B5CE7] border-0 text-sm px-3 py-1">
            {leads.length} Lead{leads.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              placeholder="Search leads by name, organization, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#F8FAFC] border-[#E2E8F0]"
              data-testid="leads-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      {loading ? (
        <div className="text-center py-12 text-[#94A3B8]">Loading leads...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0]">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-[#0F172A] font-semibold text-lg mb-2">
              {leads.length === 0 ? 'No leads yet' : 'No matching leads'}
            </h3>
            <p className="text-[#64748B] mb-4">
              {leads.length === 0
                ? 'Share the public resource page to start collecting leads.'
                : 'Try adjusting your search.'}
            </p>
            {leads.length === 0 && (
              <Button variant="outline" className="border-[#6B5CE7] text-[#6B5CE7]" onClick={copyShareLink}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Copy Public Link
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Organization</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Date</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead) => (
                  <tr key={lead.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-5 py-4">
                      <span className="text-sm font-medium text-[#0F172A]">{lead.full_name}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-1.5 text-sm text-[#475569]">
                        <Building2 className="w-3.5 h-3.5 text-[#94A3B8]" />
                        {lead.organization}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-sm text-[#007AFF] hover:underline">
                        <Mail className="w-3.5 h-3.5" />
                        {lead.email}
                      </a>
                    </td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-1.5 text-sm text-[#94A3B8]">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-[#94A3B8] hover:text-[#FF3B30]"
                        onClick={() => handleDelete(lead.id)}
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ResourceLeadsPage;
